import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Shield, Smartphone, Key, Copy, Check, AlertTriangle } from 'lucide-react';
import QRCode from 'qrcode';


interface TwoFactorAuthProps {
  onSetupComplete: (secret: string, backupCodes: string[]) => void;
  onVerificationComplete: (isValid: boolean) => void;
  mode: 'setup' | 'verify';
  userEmail?: string;
}

export const TwoFactorAuth: React.FC<TwoFactorAuthProps> = ({
  onSetupComplete,
  onVerificationComplete,
  mode,
  userEmail = 'admin@beehive.local'
}) => {
  const [step, setStep] = useState<'generate' | 'verify' | 'backup'>('generate');
  const [secret, setSecret] = useState<string>('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const generateSecret = useCallback(() => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setSecret(result);
    
    const otpAuthUrl = `otpauth://totp/Beehive%20Admin:${encodeURIComponent(userEmail)}?secret=${result}&issuer=Beehive`;
    
    QRCode.toDataURL(otpAuthUrl)
      .then(url => setQrCodeUrl(url))
      .catch(err => console.error('QR Code generation failed:', err));
  }, [userEmail]);

  useEffect(() => {
    if (mode === 'setup' && step === 'generate') {
      generateSecret();
    }
  }, [mode, step, generateSecret]);

  const generateBackupCodes = (): string[] => {
    const codes: string[] = [];
    for (let i = 0; i < 8; i++) {
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();
      codes.push(code);
    }
    return codes;
  };

  const verifyTOTP = (token: string, secret: string): boolean => {
    const window = Math.floor(Date.now() / 30000);
    
    for (let i = -1; i <= 1; i++) {
      const timeWindow = window + i;
      const hmac = generateHMAC(secret, timeWindow);
      const code = generateTOTP(hmac);
      
      if (code === token.replace(/\s/g, '')) {
        return true;
      }
    }
    return false;
  };

  const generateHMAC = (_secret: string, timeWindow: number): string => {
    const data = new ArrayBuffer(8);
    const view = new DataView(data);
    view.setUint32(4, timeWindow, false);
    
    return btoa(String.fromCharCode(...new Uint8Array(data)));
  };

  const generateTOTP = (hmac: string): string => {
    const hash = atob(hmac);
    const offset = hash.charCodeAt(hash.length - 1) & 0xf;
    const code = ((hash.charCodeAt(offset) & 0x7f) << 24) |
                 ((hash.charCodeAt(offset + 1) & 0xff) << 16) |
                 ((hash.charCodeAt(offset + 2) & 0xff) << 8) |
                 (hash.charCodeAt(offset + 3) & 0xff);
    
    return (code % 1000000).toString().padStart(6, '0');
  };



  const handleVerification = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (mode === 'setup') {
        const isValid = verifyTOTP(verificationCode, secret);
        
        if (isValid) {
          const codes = generateBackupCodes();
          setBackupCodes(codes);
          setStep('backup');
        } else {
          setError('Invalid verification code. Please try again.');
        }
      } else {
        const storedSecret = localStorage.getItem('beehive-2fa-secret');
        if (!storedSecret) {
          setError('2FA not set up. Please contact administrator.');
          return;
        }
        
        const isValid = verifyTOTP(verificationCode, storedSecret);
        onVerificationComplete(isValid);
        
        if (!isValid) {
          setError('Invalid verification code. Please try again.');
        }
      }
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = () => {
    localStorage.setItem('beehive-2fa-secret', secret);
    localStorage.setItem('beehive-2fa-backup-codes', JSON.stringify(backupCodes));
    localStorage.setItem('beehive-2fa-enabled', 'true');
    onSetupComplete(secret, backupCodes);
  };

  const copyToClipboard = (text: string, type: 'secret' | 'backup') => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'secret') {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      } else {
        setCopiedBackup(true);
        setTimeout(() => setCopiedBackup(false), 2000);
      }
    });
  };

  if (mode === 'verify') {
    return (
      <div className="glass-dark bg-gradient-to-br from-slate-900/90 to-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-lg p-8 max-w-md mx-auto">
        <div className="text-center mb-6">
          <div className="p-3 bg-blue-500/20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Shield className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Two-Factor Authentication</h2>
          <p className="text-slate-400">Enter your 6-digit verification code</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full p-3 bg-slate-800 border border-slate-600 rounded-lg text-white text-center text-2xl tracking-widest focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              placeholder="000000"
              maxLength={6}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-sm text-red-300">{error}</span>
            </div>
          )}

          <button
            onClick={handleVerification}
            disabled={verificationCode.length !== 6 || loading}
            className="w-full p-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-dark bg-gradient-to-br from-slate-900/90 to-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-lg p-8 max-w-2xl mx-auto">
      {step === 'generate' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="p-3 bg-green-500/20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Smartphone className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Set Up Two-Factor Authentication</h2>
          <p className="text-slate-400 mb-8">Scan the QR code with your authenticator app</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg">
                {qrCodeUrl && <img src={qrCodeUrl} alt="2FA QR Code" className="w-full" />}
              </div>
              
              <div className="text-left">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Manual Entry Key
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-slate-800 border border-slate-600 rounded text-slate-300 text-sm font-mono break-all">
                    {secret}
                  </code>
                  <button
                    onClick={() => copyToClipboard(secret, 'secret')}
                    className="p-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
                  >
                    {copiedSecret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full p-3 bg-slate-800 border border-slate-600 rounded-lg text-white text-center text-xl tracking-widest focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-300">{error}</span>
                </div>
              )}

              <button
                onClick={handleVerification}
                disabled={verificationCode.length !== 6 || loading}
                className="w-full p-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
              >
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {step === 'backup' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="p-3 bg-yellow-500/20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Key className="w-8 h-8 text-yellow-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Save Your Backup Codes</h2>
          <p className="text-slate-400 mb-8">Store these codes in a safe place. You can use them to access your account if you lose your device.</p>

          <div className="bg-slate-800 border border-slate-600 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-2 gap-3">
              {backupCodes.map((code, index) => (
                <div key={index} className="font-mono text-slate-300 text-center p-2 bg-slate-700 rounded">
                  {code}
                </div>
              ))}
            </div>
            
            <button
              onClick={() => copyToClipboard(backupCodes.join('\n'), 'backup')}
              className="mt-4 flex items-center gap-2 mx-auto px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
            >
              {copiedBackup ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copiedBackup ? 'Copied!' : 'Copy All Codes'}
            </button>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <span className="font-medium text-yellow-400">Important</span>
            </div>
            <p className="text-yellow-300/80 text-sm">
              Each backup code can only be used once. Keep them secure and don't share them with anyone.
            </p>
          </div>

          <button
            onClick={handleSetupComplete}
            className="w-full p-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
          >
            Complete Setup
          </button>
        </motion.div>
      )}
    </div>
  );
};
